<template>
  <div class="flex flex-col pb-4">
    <div class="text-xs text-gray-500 mb-1">
      לפי התקופה הנבחרת · <b class="text-gray-800">{{ periodLabel }}</b>
    </div>

    <button
      v-for="(report, i) of reports"
      :key="report.key"
      type="button"
      class="flex items-center gap-3 py-3.5 text-right"
      :class="{'border-b border-gray-100': i < reports.length - 1}"
      :disabled="isExporting[report.key]"
      @click="exportFile(report.key)"
    >
      <span class="w-11 h-11 rounded-[13px] shrink-0 flex items-center justify-center" :style="{background: report.tint}">
        <i :class="['pi', report.icon]" class="text-lg" :style="{color: report.fg}"/>
      </span>
      <span class="flex-1 min-w-0">
        <span class="block text-[14px] font-semibold text-gray-900">{{ report.title }}</span>
        <span class="block text-[11.5px] text-gray-500 mt-0.5">{{ report.sub }}</span>
      </span>
      <span
        class="text-[10px] font-bold rounded-full px-2.5 py-1"
        :style="{background: report.badgeBg, color: report.badgeFg}"
      >{{ report.badge }}</span>
      <i v-if="isExporting[report.key]" class="pi pi-spinner pi-spin text-sm text-gray-400"/>
    </button>
  </div>
</template>

<script lang="ts" setup>
import {reactive} from 'vue';
import {useToast} from 'vue-toastification';
import {Directory, Filesystem} from '@capacitor/filesystem';

import {PaymentsApi} from '@app/api/admin';
import {isNative} from '@app/utils/capacitor';
import {blobToBase64} from '@app/utils/utils';
import {trackEvent} from '@app/composables/posthog';

type ReportKey = 'transactions' | 'invoices' | 'tikbulim' | 'z';

const props = defineProps<{
  startDate: string;
  endDate: string;
  periodLabel: string;
}>();

const toast = useToast();

const isExporting = reactive<Record<ReportKey, boolean>>({
  transactions: false,
  invoices: false,
  tikbulim: false,
  z: false,
});

const reports: Array<{
  key: ReportKey; title: string; sub: string; badge: string;
  icon: string; tint: string; fg: string; badgeBg: string; badgeFg: string;
}> = [
  {
    key: 'transactions', title: 'כל ההכנסות', sub: 'גיליון Excel — שורה לכל עסקה',
    badge: 'XLSX', icon: 'pi-table', tint: '#dcfce7', fg: '#15803d', badgeBg: '#dcfce7', badgeFg: '#15803d',
  },
  {
    key: 'invoices', title: 'כל החשבוניות', sub: 'קובץ ZIP — יישלח למייל שלך',
    badge: 'ZIP', icon: 'pi-box', tint: '#fef3c7', fg: '#b45309', badgeBg: '#fef3c7', badgeFg: '#b45309',
  },
  {
    key: 'tikbulim', title: 'תקבולים', sub: 'הכנסה לפי אמצעי תשלום',
    badge: 'XLSX', icon: 'pi-credit-card', tint: '#edeafd', fg: '#6831d0', badgeBg: '#edeafd', badgeFg: '#6831d0',
  },
  {
    key: 'z', title: 'דוח Z', sub: 'סיכום יומי של התקבולים, אמצעי תשלום ומכירות',
    badge: 'XLSX', icon: 'pi-file', tint: '#e0f2fe', fg: '#0369a1', badgeBg: '#e0f2fe', badgeFg: '#0369a1',
  },
];

const downloadBlob = async (blob: Blob, filename: string) => {
  if (isNative()) {
    const base64Data = await blobToBase64(blob);
    await Filesystem.writeFile({
      path: filename,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true,
    });
  } else {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }
};

const exportFile = async (type: ReportKey) => {
  trackEvent('payments_export', {type});
  isExporting[type] = true;
  try {
    const params = {startDate: props.startDate, endDate: props.endDate};
    let blob: Blob;
    let filename: string;

    if (type === 'invoices') {
      // Invoices download slowly from the invoice provider, so the zip is built in the
      // background and emailed rather than downloaded inline.
      const { email } = await PaymentsApi.requestInvoicesZipByEmail(params);
      toast.success(`הייצוא בהכנה ויישלח למייל ${email} בסיום`);
      return;
    }

    if (type === 'tikbulim') {
      blob = await PaymentsApi.exportTikbulim(params);
      filename = 'tikbulim.xlsx';
    } else if (type === 'z') {
      blob = await PaymentsApi.exportZReport(params);
      filename = `z-report_${props.startDate}_${props.endDate}.xlsx`;
    } else {
      blob = await PaymentsApi.exportTransactions(params);
      filename = 'transactions.xlsx';
    }

    await downloadBlob(blob, filename);
    toast.success('הקובץ הורד בהצלחה');
  } catch (e: any) {
    console.error(e);
    if (e?.status === 404 || e?.statusCode === 404) {
      toast.info('לא נמצאו חשבוניות בטווח שנבחר');
    } else {
      toast.error('לא הצלחנו להוריד את הקובץ');
    }
  } finally {
    isExporting[type] = false;
  }
};
</script>
